<template>
  <div class="live-trainings-page">
    <HeroSection
      title="Data Science Live Training"
      subtitle="Accelerate your data-driven growth by upskilling your workforce in crucial data science practices with our expert-led live corporate group sessions. Nurture a collaborative environment with 365 Data Science Training and help your company improve business operations by enabling employees to make better informed decisions and increase efficiency."
      columns="col-12"
      button-link="signup"
      button-title="Request Live Training"
      class-name="live-trainings"
    >
      <template slot="image">
        <img
          :src="`${require('@itbpbg/lms-components/src/assets/images/live-training-new.png')}`"
          class="desktop-image"
        >
        <img
          :src="`${require('@itbpbg/lms-components/src/assets/images/live-training-mobile-new.png')}`"
          class="mobile-image"
        >
      </template>
    </HeroSection>
    <div class="container">
      <div class="trusted-companies">
        <p class="h6">
          Trusted by global leaders from:
        </p>
        <div class="logos">
          <span class="logo">
            <LogoOracle />
          </span>
          <span class="logo">
            <LogoAmazon />
          </span>
          <span class="logo">
            <LogoWalmart />
          </span>
          <span class="logo">
            <LogoVisa />
          </span>
          <span class="logo">
            <LogoAzercell />
          </span>
        </div>
      </div>
    </div>
    <div class="site-section">
      <DataScienceCards :options="dataScienceCardsOptions">
        <IconMonitor slot="card1-icon" />
        <IconMedal slot="card2-icon" />
        <IconPeople slot="card3-icon" />
      </DataScienceCards>
    </div>
    <div class="site-section dark">
      <div class="container">
        <h2 class="h2 list-title">
          Data Science Live Training
        </h2>
      </div>
      <List dark />
      <div class="button-holder">
        <button class="btn-primary btn-lg" @click="scrollToLastSection">
          Request Live Training
        </button>
      </div>
    </div>
    <div class="site-section gray">
      <InstructorSlider
        instructorslinks
        :instructors="instructors"
        :options="instructorsSliderOptions"
      />
    </div>
    <div class="site-section">
      <FrequentlyAskedQuestions group="livetraining" />
    </div>
    <GetAccess
      :features="features"
      get-access-title="Register to Book a Data Science Live Training"
      get-access-description="Take the first step to transform your organization with our data science group corporate training led by prominent data professionals. Sign up below for more information on pricing and how to book a session, or contact us at team@365datascience.com to learn more about how our live training can benefit your business."
      button-text="Sign Up Now"
      button-link="signup"
    />
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import HeroSection from '@itbpbg/lms-components/src/components/HeroSection';
import List from '@itbpbg/lms-components/src/Business/LiveTrainings/List';
import FrequentlyAskedQuestions from '@itbpbg/lms-components/src/FrequentlyAskedQuestions';
import DataScienceCards from '../../components/Landing/DataScienceCards';
import InstructorSlider from '../../components/Landing/InstructorSlider';
import GetAccess from '../../components/Business/GetAccess';
import LogoOracle from '../../assets/svg/companies/logo-oracle.svg?vue-component';
import LogoAmazon from '../../assets/svg/companies/logo-amazon.svg?vue-component';
import LogoWalmart from '../../assets/svg/companies/logo-walmart.svg?vue-component';
import LogoAzercell from '../../assets/svg/companies/logo-azercell.svg?vue-component';
import LogoVisa from '../../assets/svg/companies/logo-visa.svg?vue-component';
import IconMonitor from '../../assets/svg/icon-monitor.svg?vue-component';
import IconMedal from '../../assets/svg/icon-medal2.svg?vue-component';
import IconPeople from '../../assets/svg/icon-people.svg?vue-component';

export default {
  components: {
    HeroSection,
    List,
    DataScienceCards,
    InstructorSlider,
    FrequentlyAskedQuestions,
    GetAccess,
    LogoOracle,
    LogoAmazon,
    LogoWalmart,
    LogoAzercell,
    LogoVisa,
    IconMonitor,
    IconMedal,
    IconPeople,
  },
  mixins: [global],
  // middleware({ redirect }) {
  //   redirect('/');
  // },
  async asyncData({
    $axios, $config, route, error,
  }) {
    if ($config.platform === 'fa') {
      return error({ statusCode: 404, message: 'Page not found' });
    }
    const [instructors, page] = await Promise.all([
      $axios.$get('/team/speaker?instructorsPriority=true').catch(() => ({})),
      $axios.$get(`/pages/${route.fullPath}`).catch(() => ({})),
    ]);

    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.created = page.created || new Date();
    page.updated = page.updated || new Date();

    return {
      instructors,
      page,
    };
  },
  data() {
    return {
      heroImage: '',
      dataScienceCardsOptions: {
        title: 'Equip Your Company with Essential Data Skills',
        subtitle: 'Secure your company’s place in tomorrow’s data-driven world with live data science training from celebrated industry professionals. Our rich 365 Live Training library helps you grow a data-driven culture within your organization. It ensures you gain a competitive advantage in today’s dynamic environment—no matter your workforce’s data literacy level. Depending on your team’s calendars, you’ll also benefit from flexible scheduling. As a result of our data science group corporate training, your team members will learn to extract and interpret valuable insights to grow your business through leading data science practices.',
        firstCardTitle: 'Extensive Library of Topics',
        firstCardText: 'Explore our data science corporate group training list designed to meet your specific data needs and bridge your employees’ skills gap according to their current data capabilities. Each live training attendee will receive a certificate for their participation.',
        secondCardTitle: 'Renowned Industry Experts',
        secondCardText: 'Learn valuable data skills from seasoned data scientists whose long-standing experience allows them to translate complicated technological concepts in simple, easy-to-understand ways.',
        thirdCardTitle: 'Real-Time Guided Learning',
        thirdCardText: 'Gain a better understanding of data science and machine learning concepts and ask role-specific questions with help from your instructors in real-time during the allocated Q&A sessions.',
      },
      instructorsSliderOptions: {
        title: 'Meet Your Data Science Live Training Instructors',
        subtitle: 'We collaborate with data scientists who have worked at some of the world’s largest companies, as well as thought leaders and passionate speakers within the community. These specialists are known for their strong track record of increasing revenue, reducing costs, and improving customer satisfaction for businesses they\'ve worked with. Their expertise will enable your organization to streamline its business growth with data. Our instructors help you gain a crucial understanding of data science to make more informed, data-driven decisions by training your workforce in cutting-edge data skills to solve real-world business challenges.',
        className: 'landing',
      },
      features: [
        { name: 'Extensive List of Topics' },
        { name: 'Renowned Industry Experts' },
        { name: 'Community-Oriented Active Learning' },
        { name: 'Training for Various Skill Levels and Expertise' },
        { name: 'Certificates of Achievement' },

      ],
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps({ ...this.page }),
      script: [linkedData],
    };
  },
  methods: {
    scrollToLastSection() {
      const getAccess = document.querySelector('.get-access');
      const offsetHeader = this.$store.state.layout === 'desktop' ? 80 : 68;
      const y = getAccess.getBoundingClientRect().top + window.scrollY - offsetHeader;
      window.scrollTo({ top: y, behavior: 'smooth' });
    },
  },
};
</script>

<style lang="scss">
.top-banner + .live-trainings-page {
  padding-top: 0;
}

.live-trainings-page {
  padding-top: var(--spacing-80);

  .hero-section-wrapper.live-trainings {
    .mobile-image {
      display: none;
    }

    .wall {
      height: 700px;

      img {
        height: 350px;
        object-fit: contain;
      }
    }

    .container {
      &.image-container {
        height: 700px;
      }

      .row > div {
        .subtitle {
          max-width: 990px;
        }

        .btn-primary {
          max-width: 340px;
        }
      }
    }
  }

  .trusted-companies {
    margin-top: var(--spacing-40);
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    text-align: center;
    color: var(--main-dark-light-4);

    .logos {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: var(--spacing-18);

      .logo {
        svg {
          height: 30px;

          path[fill] {
            fill: var(--main-light-shade-4);
          }
        }
      }
    }
  }

  .site-section {
    padding: var(--spacing-120) 0;

    &.gray {
      background-color: var(--main-light-shade-1);
    }

    .data-science-cards {
      .cards {
        margin-top: var(--spacing-48);

        .card {
          margin-top: 0;
          height: 100%;
        }
      }
    }
  }

  .list-title {
    margin: 0;
  }

  .live-trainings-list {
    padding: var(--spacing-40) 0;
  }

  .button-holder {
    display: flex;
    justify-content: center;
    align-items: center;

    .btn-primary {
      width: 100%;
      max-width: 345px;
    }
  }

  .slider-container.landing {
    .slider-header {
      .heading .h3 {
        max-width: 800px;
        flex: 1 1 100%;
      }

      .subtitle p {
        max-width: 760px;
        flex: 1 1 100%;
        font-size: 18px;
      }

      .controls {
        margin-left: auto;
      }
    }
  }
}

@media only screen and (min-width: 601px) and (max-width: 783px) {
  .live-trainings-page {
    .live-trainings {
      .wall {
        height: 750px !important;
      }

      .container.image-container {
        height: 750px !important;
      }
    }
  }
}

@include bp($bp-mobile) {
  .live-trainings-page {
    padding-top: 68px;

    .hero-section-wrapper.live-trainings .wall img {
      height: auto;
      object-fit: cover;
    }

    .site-section {
      padding: var(--spacing-80) 0;
    }

    .trusted-companies .logos {
      flex-direction: column;

      .logo:not(:first-of-type) {
        margin-top: var(--spacing-24);
      }
    }

    .data-science-cards {
      .heading {
        .h2 {
          font-size: 32px;
        }

        span {
          font-size: 21px;
          color: var(--main-dark-light-3);
        }
      }

      .cards {
        .card {
          padding-left: 27px;
          padding-right: 27px;
        }

        .col {
          &:not(:last-child) {
            margin-bottom: 30px;
          }
        }
      }
    }

    .button-holder .btn-primary {
      max-width: 328px;
    }

    .slider-container.landing .slider-header {
      p {
        margin-bottom: var(--spacing-32);
      }

      .controls {
        margin: 0;
      }
    }
  }
}

@include bp($bp-phone) {
  .live-trainings-page {
    .hero-section-wrapper.live-trainings {
      .wall {
        height: auto;
      }

      .container .image-container {
        height: auto;
      }

      .mobile-image {
        display: block;
      }

      .desktop-image {
        display: none;
      }
    }
  }
}
</style>
